import { throttle } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';

interface ScrollState {
  isAtTop: boolean;
  isAtBottom: boolean;
}

export const useScrollPosition = <T extends HTMLElement = HTMLDivElement>(
  throttleMs = 100,
) => {
  const [scrollState, setScrollState] = useState<ScrollState>({
    isAtTop: true,
    isAtBottom: false,
  });
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const checkScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = element;

      setScrollState({
        isAtTop: scrollTop === 0,
        isAtBottom: scrollTop + clientHeight >= scrollHeight - 1,
      });
    };

    const throttledCheck = throttle(checkScroll, throttleMs, {
      leading: true,
      trailing: true,
    });

    checkScroll();

    element.addEventListener('scroll', throttledCheck, { passive: true });

    return () => {
      element.removeEventListener('scroll', throttledCheck);
      throttledCheck.cancel();
    };
  }, [throttleMs]);

  return [ref, scrollState] as const;
};
